/**
 * useAlertChannels — team alert-delivery channels for a project.
 *
 * Team channels (email/Telegram/webhook/Bitrix24 chat/in-app + severity
 * threshold) are a separate concept from personal subscriptions
 * (NotificationChannelsSection) and from CRM delivery rules
 * (DeliveryRulesTab) — this hook covers only the team-channel config.
 *
 * Backend is complete and unchanged
 * (docs/plans/2026-07-27-team-alert-channels-ui.md): GET/PUT/POST
 * /api/v1/projects/:id/alert-notification-channels[/test].
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { alertNotificationChannelsSchema } from '@aica/api-types/schemas';
import type { AlertNotificationChannels } from '@aica/api-types/schemas';
import { useApiClient } from '@/hooks/useApiClient';
import type { ApiClient } from '@/lib/api';
import { logError } from '@/lib/logger';

export function alertChannelsQueryKey(projectId: string) {
  return ['project', projectId, 'alert-channels'] as const;
}

type AlertChannelsFetchResult =
  { ok: true; data: AlertNotificationChannels } | { ok: false };

/**
 * Fetches and validates the config with safeParse — deliberately does NOT
 * throw on a shape mismatch (unlike BaseApiClient's validatedRequest()): a
 * stale/drifted contract here must degrade to "channels unavailable," not
 * blank the whole settings section. Logged once per actual fetch (not per
 * render — this runs inside queryFn, not the hook body).
 */
async function fetchAlertChannels(
  apiClient: ApiClient,
  projectId: string
): Promise<AlertChannelsFetchResult> {
  const response = await apiClient.projects.getAlertNotificationChannels(projectId);
  const parsed = alertNotificationChannelsSchema.safeParse(response.channels);

  if (!parsed.success) {
    logError('alert-channels.invalid_response_shape', {
      component: 'useAlertChannels',
      projectId,
      issues: parsed.error.issues.map((i) => ({
        path: i.path.join('.'),
        message: i.message,
      })),
    });
    return { ok: false };
  }

  return { ok: true, data: parsed.data };
}

export function useAlertChannels(projectId: string) {
  const { apiClient } = useApiClient();

  const query = useQuery({
    queryKey: alertChannelsQueryKey(projectId),
    queryFn: () => fetchAlertChannels(apiClient, projectId),
    enabled: !!projectId,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  return {
    data: query.data?.ok ? query.data.data : undefined,
    isLoading: query.isLoading,
    // True on a network/auth error (query.isError, thrown by the API
    // client itself) OR on a successful fetch that failed schema
    // validation (query.data.ok === false) — either way the caller has
    // nothing usable to render.
    isError: query.isError || query.data?.ok === false,
  };
}

/**
 * Full replace of alertNotificationChannels — the caller must always send
 * all six keys (see the plan's Global Constraints); this hook does not
 * merge with the existing config, it just relays whatever the caller built.
 */
export function useSaveAlertChannels(projectId: string) {
  const queryClient = useQueryClient();
  const { apiClient } = useApiClient();

  return useMutation({
    mutationFn: (data: AlertNotificationChannels) =>
      apiClient.projects.updateAlertNotificationChannels(projectId, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: alertChannelsQueryKey(projectId),
      });
    },
  });
}

export function useTestAlertChannel(projectId: string) {
  const { apiClient } = useApiClient();

  return useMutation({
    mutationFn: ({
      channel,
    }: {
      channel: 'email' | 'telegram' | 'webhook' | 'bitrix24_chat';
    }) => apiClient.projects.testAlertNotificationChannel(projectId, channel),
  });
}
